import React, { useState } from 'react';
import {
  Shield, CheckCircle, AlertTriangle, XCircle, Clock,
  DollarSign, ExternalLink, FileText, X, Bug,
} from 'lucide-react';
import type { GuardianSSEEvent, GuardianButtonState, GuardianProgressPhase } from '../../hooks/useGuardianSSE';

// ─── Progress Phase Labels ──────────────────────────────────────

const PHASE_LABELS: Record<GuardianProgressPhase, string> = {
  fetching_baseline: 'Fetching test baseline...',
  running_tests: 'Running test suite...',
  analyzing: 'Analyzing results...',
  fixing: 'Attempting auto-fix...',
  complete: 'Complete',
  error: 'Error',
};

const PHASE_ICONS: Record<GuardianProgressPhase, string> = {
  fetching_baseline: '📥',
  running_tests: '🧪',
  analyzing: '🔍',
  fixing: '🔧',
  complete: '✅',
  error: '❌',
};

// ─── Regression Item ────────────────────────────────────────────

interface RegressionItemProps {
  regression: Record<string, unknown>;
  onCreateIssue?: (regression: Record<string, unknown>) => void;
  onViewLogs?: (regression: Record<string, unknown>) => void;
  onDismiss?: (regression: Record<string, unknown>) => void;
}

const RegressionItem: React.FC<RegressionItemProps> = ({
  regression, onCreateIssue, onViewLogs, onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const testName = (regression.test_name as string) || 'Unknown test';
  const testFile = (regression.test_file as string) || '';
  const errorMsg = (regression.error_message as string) || '';
  const fixAttempted = regression.fix_attempted as boolean;
  const fixSucceeded = regression.fix_succeeded as boolean;

  return (
    <div style={{
      padding: '8px 10px',
      background: 'var(--surface2)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      marginBottom: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Bug size={12} style={{ color: 'var(--red)', marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)',
            wordBreak: 'break-word',
          }}>
            {testName}
          </div>
          {testFile && (
            <div style={{
              fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
              color: 'var(--text-muted)', marginTop: 2,
            }}>
              {testFile}
            </div>
          )}
          {errorMsg && (
            <div style={{
              fontSize: 'var(--text-xs)', color: 'var(--red)',
              marginTop: 4, fontFamily: 'var(--font-mono)',
              padding: '4px 6px', background: 'var(--red-dim)',
              borderRadius: 'var(--radius-sm)', wordBreak: 'break-word',
            }}>
              {errorMsg}
            </div>
          )}
          {fixAttempted && (
            <div style={{
              fontSize: 'var(--text-xs)', marginTop: 4,
              color: fixSucceeded ? 'var(--green)' : 'var(--text-muted)',
            }}>
              {fixSucceeded ? '✅ Auto-fixed' : '⚠️ Auto-fix attempted but failed'}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{
        display: 'flex', gap: 6, marginTop: 8,
        justifyContent: 'flex-end',
      }}>
        {onViewLogs && (
          <button
            className="btn btn-sm"
            onClick={() => onViewLogs(regression)}
            style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--text-xs)' }}
          >
            <FileText size={10} /> View Logs
          </button>
        )}
        {onCreateIssue && (
          <button
            className="btn btn-sm"
            onClick={() => onCreateIssue(regression)}
            style={{
              display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--text-xs)',
              background: 'var(--orange-dim)', borderColor: 'var(--orange)', color: 'var(--orange)',
            }}
          >
            <ExternalLink size={10} /> Create Issue
          </button>
        )}
        {onDismiss && (
          <button
            className="btn btn-sm"
            onClick={() => { setDismissed(true); onDismiss(regression); }}
            style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 'var(--text-xs)' }}
          >
            <X size={10} /> Dismiss
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Props ──────────────────────────────────────────────────────

interface GuardianChatBubbleProps {
  events: GuardianSSEEvent[];
  buttonState: GuardianButtonState;
  isStreaming: boolean;
  onCreateIssue?: (regression: Record<string, unknown>) => void;
  onViewLogs?: (regression: Record<string, unknown>) => void;
  onDismiss?: (regression: Record<string, unknown>) => void;
}

// ─── Main Component ─────────────────────────────────────────────

export const GuardianChatBubble: React.FC<GuardianChatBubbleProps> = ({
  events, buttonState, isStreaming, onCreateIssue, onViewLogs, onDismiss,
}) => {
  // Extract progress phases from events
  const progressPhases: GuardianProgressPhase[] = [];
  const seenPhases = new Set<string>();
  for (const evt of events) {
    if (evt.progress_phase && !seenPhases.has(evt.progress_phase)) {
      seenPhases.add(evt.progress_phase);
      progressPhases.push(evt.progress_phase);
    }
  }

  // Find terminal event
  const terminalEvent = events.find(e =>
    e.status === 'complete_clean' ||
    e.status === 'complete_regressions' ||
    e.status === 'error' ||
    e.status === 'timed_out'
  );

  const regressions = (terminalEvent?.regressions ?? []) as Record<string, unknown>[];
  const testSummary = terminalEvent?.test_summary;

  // Determine header color
  const headerColor =
    buttonState === 'complete_clean' ? 'var(--green)' :
    buttonState === 'complete_regressions' ? 'var(--orange)' :
    buttonState === 'error' || buttonState === 'timed_out' ? 'var(--red)' :
    'var(--purple)';

  const headerBg =
    buttonState === 'complete_clean' ? 'var(--green-dim)' :
    buttonState === 'complete_regressions' ? 'var(--orange-dim)' :
    buttonState === 'error' || buttonState === 'timed_out' ? 'var(--red-dim)' :
    'var(--purple-dim)';

  return (
    <div className="msg-bubble msg-bubble-system" style={{ borderColor: headerColor }}>
      {/* Author line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
      }}>
        <Shield size={14} style={{ color: headerColor }} />
        <span className="msg-meta-sender" style={{ color: headerColor, fontWeight: 700 }}>
          GUARDIAN
        </span>
        {terminalEvent?.completed_at && (
          <span className="msg-meta-time">
            {new Date(terminalEvent.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Progress phases */}
      {progressPhases.map((phase, i) => {
        const isLast = i === progressPhases.length - 1;
        const isDone = !isLast || !!terminalEvent;
        return (
          <div key={phase} className="phase-row" style={{ marginBottom: 4 }}>
            <span className="phase-icon">
              {isDone ? '✅' : PHASE_ICONS[phase]}
            </span>
            <span className={`phase-label ${isDone ? 'done' : 'running'}`}>
              {PHASE_LABELS[phase]}
            </span>
          </div>
        );
      })}

      {/* Empty error state — no events received, show helpful message */}
      {!isStreaming && !terminalEvent && progressPhases.length === 0 && (buttonState === 'error' || buttonState === 'timed_out') && (
        <div style={{
          fontSize: 'var(--text-sm)', color: 'var(--text-muted)',
          padding: '6px 0',
        }}>
          {buttonState === 'timed_out'
            ? 'Guardian timed out before completing. This can happen if the test suite takes too long. Try again or check your test configuration.'
            : 'Guardian encountered an error before it could start. This usually means the test infrastructure is not configured yet. Click Retry above to try again.'
          }
        </div>
      )}

      {/* Streaming indicator */}
      {isStreaming && !terminalEvent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--purple)', animation: 'pulse 1s infinite' }} />
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--purple)', animation: 'pulse 1s infinite 0.15s' }} />
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--purple)', animation: 'pulse 1s infinite 0.3s' }} />
        </div>
      )}

      {/* Terminal state: test summary */}
      {terminalEvent && testSummary && (
        <div style={{
          marginTop: 8, padding: '8px 10px',
          background: headerBg,
          border: `1px solid ${headerColor}`,
          borderRadius: 'var(--radius-md)',
        }}>
          <div style={{
            fontSize: 'var(--text-sm)', fontWeight: 600, color: headerColor,
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
          }}>
            {buttonState === 'complete_clean' ? (
              <><CheckCircle size={14} /> All Tests Passing</>
            ) : buttonState === 'complete_regressions' ? (
              <><AlertTriangle size={14} /> Regressions Detected</>
            ) : buttonState === 'timed_out' ? (
              <><Clock size={14} /> Run Timed Out</>
            ) : (
              <><XCircle size={14} /> Run Failed</>
            )}
          </div>

          {/* Test counts */}
          <div style={{
            display: 'flex', gap: 12, fontSize: 'var(--text-xs)',
            fontFamily: 'var(--font-mono)', color: 'var(--text-dim)',
          }}>
            <span>{testSummary.total} total</span>
            <span style={{ color: 'var(--green)' }}>{testSummary.passed} passed</span>
            <span style={{ color: 'var(--red)' }}>{testSummary.failed} failed</span>
            {testSummary.skipped > 0 && (
              <span style={{ color: 'var(--text-muted)' }}>{testSummary.skipped} skipped</span>
            )}
            {testSummary.duration_ms > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Clock size={10} />
                {testSummary.duration_ms < 1000
                  ? `${testSummary.duration_ms}ms`
                  : `${(testSummary.duration_ms / 1000).toFixed(1)}s`}
              </span>
            )}
          </div>

          {/* Regression summary */}
          {terminalEvent.regressions_found != null && terminalEvent.regressions_found > 0 && (
            <div style={{
              marginTop: 6, fontSize: 'var(--text-xs)',
              fontFamily: 'var(--font-mono)', color: 'var(--text-dim)',
            }}>
              {terminalEvent.regressions_found} regression(s) found
              {terminalEvent.regressions_fixed != null && terminalEvent.regressions_fixed > 0 && (
                <>, {terminalEvent.regressions_fixed} auto-fixed</>
              )}
              {terminalEvent.regressions_unfixable != null && terminalEvent.regressions_unfixable > 0 && (
                <>, {terminalEvent.regressions_unfixable} need attention</>
              )}
            </div>
          )}

          {/* Cost */}
          {terminalEvent.cost_usd != null && terminalEvent.cost_usd > 0 && (
            <div style={{
              marginTop: 4, fontSize: 'var(--text-xs)',
              fontFamily: 'var(--font-mono)', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: 3,
            }}>
              <DollarSign size={10} />
              ${terminalEvent.cost_usd.toFixed(2)}
            </div>
          )}
        </div>
      )}

      {/* Terminal state without test summary (error/timeout) */}
      {terminalEvent && !testSummary && (
        <div style={{
          marginTop: 8, padding: '8px 10px',
          background: headerBg,
          border: `1px solid ${headerColor}`,
          borderRadius: 'var(--radius-md)',
        }}>
          <div style={{
            fontSize: 'var(--text-sm)', fontWeight: 600, color: headerColor,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {buttonState === 'timed_out' ? (
              <><Clock size={14} /> Run Timed Out (5 min limit)</>
            ) : buttonState === 'error' ? (
              <><XCircle size={14} /> {terminalEvent.error || 'Guardian run failed'}</>
            ) : buttonState === 'complete_clean' ? (
              <><CheckCircle size={14} /> All Clear — No Regressions</>
            ) : (
              <><AlertTriangle size={14} /> Regressions Detected</>
            )}
          </div>
        </div>
      )}

      {/* Regression list with actions */}
      {regressions.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            fontSize: 'var(--text-xs)', fontWeight: 600,
            color: 'var(--text-dim)', marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Regressions ({regressions.length})
          </div>
          {regressions.map((reg, i) => (
            <RegressionItem
              key={i}
              regression={reg}
              onCreateIssue={onCreateIssue}
              onViewLogs={onViewLogs}
              onDismiss={onDismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
};
